import { useState } from 'react';

import { conferirEnsaio, ErroDeRede } from '../nucleo/api.js';
import { placarDoEnsaio } from '../nucleo/laboratorio.js';
import { lerNumero } from '../nucleo/numero.js';

/**
 * "O ensaio que está rodando vale?" — a tela que tem o direito de dizer não.
 *
 * O desenho inteiro gira em torno de uma coisa que a API aprendeu em
 * 15/10/2026: a pergunta tem TRÊS respostas, não duas. Vale, não vale, e —
 * de longe a mais comum — ainda não dá para dizer, porque nada foi informado.
 * Um booleano `aprovado` não consegue dizer a terceira, e enquanto a API só
 * tinha o booleano ela respondia `true` a um formulário em branco.
 *
 * Então aqui nada é verde por omissão. O cabeçalho mostra o veredito de três
 * estados, e logo abaixo dele vem o placar do que foi e do que NÃO foi
 * conferido — porque "aprovado em 2 de 7 critérios" e "aprovado" são frases
 * diferentes, e só a primeira é verdade.
 */
const CAMPOS = [
  {
    campo: 'rsd_replicatas_pct',
    rotulo: 'Dispersão entre replicatas (RSD)',
    unidade: '%',
    dica:
      'O desvio relativo entre as réplicas do mesmo frasco. A VDI 4630 pede réplicas; ' +
      'Holliger et al. (2016) põem o corte em 5 a 10% para o ensaio ser publicável.',
  },
  {
    campo: 'branco_pct_do_metano_total',
    rotulo: 'Contribuição do branco (inóculo)',
    unidade: '% do metano total',
    dica:
      'Quanto do metano medido veio do inóculo sozinho. Quando o branco responde por ' +
      'metade do gás, o que se mediu foi o lodo, não a amostra.',
  },
  {
    campo: 'controle_celulose',
    rotulo: 'Controle positivo de celulose',
    unidade: 'NmL CH₄/g SV',
    dica:
      'O termômetro do inóculo: celulose microcristalina tem BMP conhecido. Se o ' +
      'controle não bate, o problema é do inóculo e nenhum resultado da bateria se salva.',
  },
  {
    campo: 'bmp_medido',
    rotulo: 'BMP medido na amostra',
    unidade: 'NmL CH₄/g SV',
    dica: 'O número que o ensaio produziu — o que está sendo conferido.',
  },
  {
    campo: 'teto_boyle',
    rotulo: 'Teto teórico de Boyle',
    unidade: 'NmL CH₄/g SV',
    dica:
      'Se a composição elementar for conhecida. Medida acima do teto é impossível por ' +
      'estequiometria, e o que isso costuma indicar é erro de denominador de SV.',
  },
  {
    campo: 'proteina_pct_sv',
    rotulo: 'Teor de proteína',
    unidade: '% do SV',
    dica: 'Entra pelo risco de inibição por amônia, que aparece na digestão de proteína.',
  },
];

export function LaboratorioConferir() {
  const [valores, setValores] = useState({});
  const [parada, setParada] = useState('');
  const [resposta, setResposta] = useState(null);
  const [erro, setErro] = useState(null);
  const [conferindo, setConferindo] = useState(false);

  function enviar(evento) {
    evento.preventDefault();
    setConferindo(true);
    setErro(null);

    const corpo = {};
    for (const [campo, texto] of Object.entries(valores)) {
      const n = lerNumero(texto);
      if (n !== null) corpo[campo] = n;
    }
    // Três estados também na entrada, e não um checkbox. Um checkbox só sabe
    // dizer sim e não, e "ainda não sei se o critério de parada foi atingido"
    // é a resposta honesta de quem está com o ensaio no meio — tratá-la como
    // "não atingido" geraria um aviso grave que o ensaio não merece.
    if (parada === 'sim') corpo.criterio_1pct_em_3dias_atingido = true;
    if (parada === 'nao') corpo.criterio_1pct_em_3dias_atingido = false;

    conferirEnsaio(corpo)
      .then((r) => setResposta(r))
      .catch((e) => setErro(e))
      .finally(() => setConferindo(false));
  }

  return (
    <div className="empilha-largo">
      <section className="bloco">
        <h2 className="simulador__titulo">O ensaio que está rodando vale?</h2>
        <p className="simulador__chamada">
          Preencha só o que você tem. Cada critério em branco aparece no resultado como{' '}
          <strong>não conferido</strong> — nunca como aprovado.
        </p>
      </section>

      <form className="bloco" onSubmit={enviar}>
        <div className="grupo-de-analise__campos">
          {CAMPOS.map((c) => (
            <div className="campo" key={c.campo}>
              <label className="campo__rotulo" htmlFor={c.campo}>
                {c.rotulo} <span className="campo__opcional">{c.unidade}</span>
              </label>
              <input
                id={c.campo}
                className="campo__entrada"
                inputMode="decimal"
                value={valores[c.campo] ?? ''}
                onChange={(e) =>
                  setValores((v) => ({ ...v, [c.campo]: e.target.value }))
                }
              />
              <p className="campo__dica">{c.dica}</p>
            </div>
          ))}

          <div className="campo">
            <label className="campo__rotulo" htmlFor="parada">
              Critério de parada atingido?
            </label>
            <select
              id="parada"
              className="campo__entrada"
              value={parada}
              onChange={(e) => setParada(e.target.value)}
            >
              <option value="">ainda não sei</option>
              <option value="sim">sim — menos de 1% ao dia por 3 dias</option>
              <option value="nao">não — o ensaio ainda produz</option>
            </select>
            <p className="campo__dica">
              O critério de parada da VDI 4630 e de Holliger et al. (2016): o ensaio acaba
              quando a produção diária cai abaixo de 1% do acumulado por três dias seguidos.
              Parar antes corta a cauda e subestima o BMP.
            </p>
          </div>
        </div>

        <button type="submit" className="botao" disabled={conferindo}>
          {conferindo ? 'Conferindo…' : 'Conferir o ensaio'}
        </button>
      </form>

      {erro ? (
        <section className="bloco" role="alert">
          <h3 className="rotulo-secao">A conferência não saiu</h3>
          <p className="campo__dica campo__dica--erro">
            {erro instanceof ErroDeRede
              ? 'Não consegui falar com o servidor. Tente de novo quando a conexão voltar.'
              : erro.message}
          </p>
        </section>
      ) : null}

      {resposta ? <Conferencia resposta={resposta} /> : null}
    </div>
  );
}

/**
 * O veredito em três estados, e por que `sem_base` tem cor própria.
 *
 * Pintar "nada foi conferido" de vermelho seria acusar um ensaio que ninguém
 * examinou; pintar de verde seria o falso verde que esta tela existe para
 * matar. A terceira cor é a resposta: cinza é a cor de não saber.
 */
const VEREDITOS = {
  aprovado: {
    titulo: 'Passou nos critérios conferidos',
    porque:
      'Nenhum dos critérios que você informou levantou aviso grave. Isso não é o mesmo ' +
      'que um ensaio validado: vale pelo que foi conferido, e o placar abaixo diz quanto foi.',
  },
  reprovado: {
    titulo: 'Reprovado em pelo menos um critério',
    porque:
      'Pelo menos um aviso é grave. Publicar o número assim é publicar um resultado que ' +
      'um revisor derruba com a planilha do próprio ensaio.',
  },
  sem_base: {
    titulo: 'Não há o que aprovar nem o que reprovar',
    porque:
      'Nenhum critério foi informado. Esta é a resposta certa para um formulário em ' +
      'branco — e até 15/10/2026 esta API respondia "aprovado" aqui.',
  },
};

function Conferencia({ resposta }) {
  const placar = placarDoEnsaio(resposta);
  const v = VEREDITOS[resposta.veredito] ?? {
    titulo: resposta.veredito,
    porque: 'Veredito que esta tela ainda não conhece.',
  };

  return (
    <>
      <section className={`bloco veredito veredito--${resposta.veredito}`}>
        <h3 className="rotulo-secao">{v.titulo}</h3>
        <p className="afirmacao">{v.porque}</p>
        <p className="terciario">
          {placar.conferidos} de {placar.conferidos + placar.faltando} critérios conferidos
          {placar.graves > 0 ? ` · ${placar.graves} grave(s)` : ''}
          {placar.leves > 0 ? ` · ${placar.leves} observação(ões)` : ''}
        </p>
      </section>

      {resposta.avisos.length > 0 ? (
        <section className="bloco">
          <h3 className="rotulo-secao">O que o ensaio disse</h3>
          <div className="empilha">
            {resposta.avisos.map((a) => (
              <div key={a.codigo} className={`serie ${a.grave ? 'serie--engana' : ''}`}>
                <p className="serie__titulo">{a.grave ? 'Grave' : 'Observação'}</p>
                <p className="ficha__linha">{a.mensagem}</p>
                {a.fonte ? <p className="serie__fonte">{a.fonte}</p> : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="bloco">
        <h3 className="rotulo-secao">O que ficou de fora</h3>
        <p className="terciario">
          Critério não informado não é critério cumprido. Esta lista é o que ainda separa o
          ensaio de uma validação completa.
        </p>
        <ul className="lista-criterios">
          {resposta.conferido.map((c) => (
            <li key={c} className="lista-criterios__ok">
              {c}
            </li>
          ))}
          {resposta.nao_conferido.map((c) => (
            <li key={c} className="lista-criterios__falta">
              {c}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
